<template>
  <div v-if="visible && rect" class="spotlight-guide">
    <div class="spotlight-mask" :style="maskTop"></div>
    <div class="spotlight-mask" :style="maskLeft"></div>
    <div class="spotlight-mask" :style="maskRight"></div>
    <div class="spotlight-mask" :style="maskBottom"></div>
    <div class="spotlight-ring" :style="ringStyle"></div>
    <div class="spotlight-pop" :style="popStyle" @click.stop>
      <b>{{ title }}</b>
      <p>{{ content }}</p>
      <button type="button" class="spotlight-btn" @click="handleClose">知道了</button>
    </div>
  </div>
</template>

<script>
/**
 * @description: 漫游式高亮引导（挖洞遮罩 + 提示气泡）
 */
export default {
  name: 'SpotlightGuide',
  props: {
    visible: { type: Boolean, default: false },
    target: { type: String, required: true },
    title: { type: String, default: '' },
    content: { type: String, default: '' }
  },
  data() {
    return {
      rect: null,
      viewport: { w: 0, h: 0 }
    };
  },
  computed: {
    maskTop() {
      return this.rect ? { top: '0', left: '0', width: this.viewport.w + 'px', height: this.rect.top + 'px' } : null;
    },
    maskLeft() {
      return this.rect ? { top: this.rect.top + 'px', left: '0', width: this.rect.left + 'px', height: this.rect.height + 'px' } : null;
    },
    maskRight() {
      return this.rect ? {
        top: this.rect.top + 'px',
        left: (this.rect.left + this.rect.width) + 'px',
        width: Math.max(0, this.viewport.w - this.rect.left - this.rect.width) + 'px',
        height: this.rect.height + 'px'
      } : null;
    },
    maskBottom() {
      return this.rect ? {
        top: (this.rect.top + this.rect.height) + 'px',
        left: '0',
        width: this.viewport.w + 'px',
        height: Math.max(0, this.viewport.h - this.rect.top - this.rect.height) + 'px'
      } : null;
    },
    ringStyle() {
      return this.rect ? {
        left: (this.rect.left - 4) + 'px',
        top: (this.rect.top - 4) + 'px',
        width: (this.rect.width + 8) + 'px',
        height: (this.rect.height + 8) + 'px'
      } : null;
    },
    popStyle() {
      if (!this.rect) return null;
      return {
        left: (this.rect.left + this.rect.width / 2) + 'px',
        top: (this.rect.top + this.rect.height + 16) + 'px'
      };
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val) {
          this.$nextTick(() => {
            this.updateRect();
            window.addEventListener('resize', this.updateRect);
            window.addEventListener('scroll', this.updateRect, { passive: true });
            window.addEventListener('keydown', this.onKeydown);
          });
        } else {
          this.clearListeners();
        }
      }
    }
  },
  beforeDestroy() {
    this.clearListeners();
  },
  methods: {
    updateRect() {
      const el = document.querySelector(this.target);
      if (el && el.getBoundingClientRect) {
        const r = el.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) {
          this.rect = null;
          return;
        }
        this.rect = { left: r.left, top: r.top, width: r.width, height: r.height };
      } else {
        this.rect = null;
      }
      this.viewport = { w: window.innerWidth, h: window.innerHeight };
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.handleClose();
    },
    handleClose() {
      this.$emit('close');
    },
    clearListeners() {
      window.removeEventListener('resize', this.updateRect);
      window.removeEventListener('scroll', this.updateRect);
      window.removeEventListener('keydown', this.onKeydown);
    }
  }
};
</script>

<style lang="less" scoped>
.spotlight-guide {
  position: fixed;
  inset: 0;
  z-index: 9999;
  pointer-events: none;
}
.spotlight-mask {
  position: fixed;
  background: rgba(4, 10, 22, 0.78);
  pointer-events: auto;
}
.spotlight-ring {
  position: fixed;
  pointer-events: none;
  border: 2px solid rgba(67, 217, 255, 0.9);
  border-radius: 999px;
  animation: spotlight-pulse 1.6s ease-in-out infinite;
}
@keyframes spotlight-pulse {
  0%, 100% { box-shadow: 0 0 16px rgba(67, 217, 255, 0.45); }
  50% { box-shadow: 0 0 32px rgba(67, 217, 255, 0.75); }
}
.spotlight-pop {
  position: fixed;
  transform: translateX(-50%);
  width: 300px;
  padding: 18px 20px;
  border-radius: 16px;
  background: rgba(11, 23, 43, 0.97);
  border: 1px solid rgba(67, 217, 255, 0.35);
  box-shadow: 0 18px 50px rgba(0, 0, 0, 0.45);
  pointer-events: auto;
  animation: spotlight-fade 0.25s ease-out;
  b { display: block; color: #fff; font-size: 15px; }
  p { color: #9fb0cc; font-size: 13px; line-height: 1.7; margin-top: 6px; }
}
.spotlight-pop::before {
  content: '';
  position: absolute;
  left: 50%;
  top: -7px;
  transform: translateX(-50%) rotate(45deg);
  width: 12px;
  height: 12px;
  background: rgba(11, 23, 43, 0.97);
  border-left: 1px solid rgba(67, 217, 255, 0.35);
  border-top: 1px solid rgba(67, 217, 255, 0.35);
}
.spotlight-btn {
  margin-top: 14px;
  border: 0;
  cursor: pointer;
  padding: 9px 22px;
  border-radius: 999px;
  background: linear-gradient(110deg, #ffe08b, #ffc84e);
  color: #07101d;
  font-weight: 700;
  font-size: 13px;
}
@keyframes spotlight-fade {
  from { opacity: 0; transform: translateX(-50%) translateY(6px); }
  to { opacity: 1; transform: translateX(-50%) translateY(0); }
}
</style>
